import * as React from 'react';

import { AdminOutreachChannel } from '../../__generated__/globalTypes';
import Table from '../../components/Table';

import { CHANNEL_NAMES, deliveryCodeMeaning, failuresByFaultClass, smsFaultClassLabel } from './channelHealthView';

import type { AdminGetChannelHealthQuery } from './__generated__/ChannelHealthScene.gql';

type ChannelSummary = AdminGetChannelHealthQuery['adminGetChannelHealth']['channels'][number];

interface IChannelFailuresPanelProps {
  channels: ChannelSummary[];
}

/**
 * The window's failed outreach openings by provider code, texts first and the
 * most frequent code first within each. Above the table, a text's failures are
 * summed by whose fault their code points to, our side first: that line answers
 * "is it us?" before any code is read.
 */
const ChannelFailuresPanel = ({ channels }: IChannelFailuresPanelProps) => {
  const text = channels.find((summary) => summary.channel === AdminOutreachChannel.Sms);
  const rows = channels.flatMap((summary) =>
    summary.failureCodes.map((failure) => ({ channel: summary.channel, ...failure }))
  );

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-fg-secondary text-xs font-semibold uppercase tracking-wider">Failed openings by code</h2>

      {text !== undefined && text.receipts.failed > 0 && (
        <p className="text-fg-tertiary text-xs">
          Text failures by whose fault:{' '}
          {failuresByFaultClass(text.failureCodes).map(({ faultClass, openings }, index) => (
            <React.Fragment key={faultClass ?? 'uncoded'}>
              {index > 0 && ' · '}
              {smsFaultClassLabel(faultClass)} <span className="text-fg-primary tabular-nums">{openings}</span>
            </React.Fragment>
          ))}
        </p>
      )}

      {rows.length === 0 ? (
        <div className="text-fg-tertiary text-xs italic">No outreach opening failed in this window.</div>
      ) : (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Channel</Table.HeaderCell>
              <Table.HeaderCell>Code</Table.HeaderCell>
              <Table.HeaderCell>Meaning</Table.HeaderCell>
              <Table.HeaderCell>Fault</Table.HeaderCell>
              <Table.HeaderCell className="text-right">Openings</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((row) => (
              <Table.Row key={`${row.channel}-${row.code ?? 'uncoded'}`}>
                <Table.Cell className="text-xs">{CHANNEL_NAMES[row.channel]}</Table.Cell>
                <Table.Cell mono className="text-xs">
                  {row.code ?? <span className="text-fg-quaternary">—</span>}
                </Table.Cell>
                <Table.Cell muted className="text-xs">
                  {row.code ? (deliveryCodeMeaning(row.channel, row.code) ?? '—') : 'Reported without a code'}
                </Table.Cell>
                <Table.Cell muted className="text-xs">
                  {row.faultClass ? smsFaultClassLabel(row.faultClass) : '—'}
                </Table.Cell>
                <Table.Cell className="text-right text-xs">{row.openings}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </section>
  );
};

export default ChannelFailuresPanel;
